// lib/models/jobApplication.ts

export type ApplicationStatus = 'pending' | 'reviewing' | 'accepted' | 'rejected' | 'withdrawn';

export interface JobApplication {
    id: string;
    need_id: string;
    user_id: string;
    candidate_profile_id?: string;
    status: ApplicationStatus;
    cover_letter?: string;
    decision_note?: string;
    applied_at: string;
    reviewed_at?: string;
    reviewed_by?: string;
    created_at: string;
    updated_at: string;
    // Enrichment from the list endpoints (so the UI can show real labels).
    need_title?: string;        // the posting's title (candidate view)
    need_role?: string;         // the posting's role (candidate view)
    organisation_name?: string; // the hiring organisation (candidate view)
    organisation_logo_url?: string; // #40; absent → name-derived placeholder
    unit_name?: string;         // the hiring unit (candidate view)
    applicant_name?: string;    // the applicant's name (employer view)
}

export interface JobApplicationListResponse {
    applications: JobApplication[];
    total: number;
}

export interface CreateJobApplicationRequest {
    cover_letter?: string;
}

export interface UpdateJobApplicationStatusRequest {
    status: 'reviewing' | 'accepted' | 'rejected';
    decision_note?: string;
    // Acknowledge the "already engaged" (409 ALREADY_ENGAGED) warning and
    // deliberately re-hire someone already employed at the org+unit.
    confirm_rehire?: boolean;
}

// Public need response (slim, for job board)
export interface PublicNeed {
    id: string;
    title: string;
    role: string;
    description?: string;
    body_markdown?: string;          // rich source-of-truth body (multi-section annons)
    competencies: string[];
    min_hours_per_week: number;
    max_hours_per_week: number;
    start_date: string;
    city: string;
    work_location_on_site: boolean;
    work_location_remote: boolean;
    offers_supervision: boolean;
    organisation_id: string;
    organisation_name: string;
    organisation_logo_url?: string; // #40; absent → name-derived placeholder
    unit_name: string;
    onboarding_program_id?: string; // role-specific onboarding (informational for candidates)

    // Job posting details (#217)
    application_deadline?: string;
    employment_type: import('./need').EmploymentType;
    employment_extent: import('./need').EmploymentExtent;
    extent_percent?: number;
    positions_count: number;
    contacts: import('./need').NeedContact[];
    salary_min?: number;
    salary_max?: number;
    salary_type: import('./need').SalaryType;

    created_at: string;
    // Sent by the backend since #277 so a candidate can see a posting was
    // edited after they applied; the type omitted it until #320.
    updated_at: string;
}

export interface PublicNeedListResponse {
    needs: PublicNeed[];
    total: number;
}

// Distinct filter values for the job-board dropdowns (#39). Returned by
// GET /api/public/need-facets, computed over the whole active-need set.
export interface NeedFacetsResponse {
    cities: string[];
    competencies: string[];
}

// Structured query for the public job board (#39). Mirrors the backend
// PublicNeedFilterRequest — all fields optional; omitted == no filter.
export interface PublicNeedQuery {
    page?: number;
    limit?: number;
    q?: string;
    city?: string;
    employment_type?: string;
    employment_extent?: string;
    work_location?: string; // 'onsite' | 'remote'
    competencies?: string[];
    supervision?: boolean;
    sort?: string;          // 'recent' | 'deadline'
}

// Status labels and colors
export const ApplicationStatusLabels: Record<ApplicationStatus, string> = {
    pending: 'Inskickad',
    reviewing: 'Under granskning',
    accepted: 'Accepterad',
    rejected: 'Avvisad',
    withdrawn: 'Återkallad',
};

export const ApplicationStatusColors: Record<ApplicationStatus, string> = {
    pending: 'yellow',
    reviewing: 'blue',
    accepted: 'green',
    rejected: 'red',
    withdrawn: 'gray',
};

export const formatWorkLocation = (onSite: boolean, remote: boolean): string => {
    if (onSite && remote) return 'Hybrid';
    if (onSite) return 'På plats';
    if (remote) return 'Distans';
    return 'Ej specificerad';
};

export const formatHoursRange = (min: number, max: number): string => {
    if (min === max) return `${min} tim/v`;
    return `${min}-${max} tim/v`;
};
